<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import type { FieldVariants } from './variants';
import { computed } from 'vue';
import FieldLabel from '@/components/base/label';
import generateId from '@/utils/generateId';
import Field from './internal/Field.vue';
import FieldContent from './internal/FieldContent.vue';
import FieldDescription from './internal/FieldDescription.vue';
import FieldError from './internal/FieldError.vue';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = withDefaults(defineProps<{
    class?: HTMLAttributes['class']
    layout?: FieldVariants['layout']
    compact?: boolean
    label?: string
    labelFor?: string
    description?: string

    // shortcuts
    vertical?: boolean
    horizontal?: boolean
    responsive?: boolean
    inline?: boolean
    inlineReverse?: boolean

    // state
    required?: boolean
    invalid?: boolean
    disabled?: boolean
    errors?: string[] | null
}>(), {
    class: '',
    layout: undefined,
    compact: false,
    label: '',
    labelFor: '',
    description: '',
    required: false,
    invalid: false,
    disabled: false,
    errors: null,
});

const stableId = generateId('field');

const isInvalid = computed<boolean>(() => {
    return props.invalid || !!(props.errors && props.errors.length > 0);
});

type ShortcutKey = 'inlineReverse' | 'inline' | 'responsive' | 'horizontal' | 'vertical';

const shortcutToLayout: Record<ShortcutKey, NonNullable<FieldVariants['layout']>> = {
    inlineReverse: 'inline-reverse',
    inline: 'inline',
    responsive: 'responsive',
    horizontal: 'horizontal',
    vertical: 'vertical',
};

const layout = computed<FieldVariants['layout']>(() => {
    const shortcutKey = (Object.keys(shortcutToLayout) as ShortcutKey[]).find((key) => props[key]);
    if (shortcutKey) {
        if (props.layout) {
            console.warn('Field: Both layout prop and layout shortcut props are used. The layout prop will take precedence over the shortcuts. It is recommended to use either the layout prop or the shortcuts, but not both.');
            return props.layout;
        }

        return shortcutToLayout[shortcutKey];
    }

    return props.layout || 'vertical';
});

const fieldId = computed(() => props.labelFor || stableId);

const slotProps = computed(() => {
    return {
        required: props.required,
        invalid: isInvalid.value,
        disabled: props.disabled,
        id: fieldId.value,
    };
});
</script>

<template>
    <Field
        v-bind="$attrs"
        :layout="layout"
        :compact="props.compact"
        :class="[props.class, { 'is-multiline': props.description || (props.errors && props.errors.length > 0) }]"
        :data-invalid="isInvalid ? 'true' : undefined"
    >
        <template v-if="layout?.startsWith('inline')">
            <slot v-bind="slotProps"></slot>

            <FieldContent>
                <FieldLabel
                    v-if="props.label"
                    :for="fieldId"
                    :inline="true"
                >
                    {{ props.label }}{{ props.required ? '*' : '' }}
                </FieldLabel>

                <FieldDescription v-if="props.description">
                    {{ props.description }}
                </FieldDescription>

                <FieldError
                    v-if="props.errors && props.errors.length > 0"
                    :errors="props.errors"
                />
            </FieldContent>
        </template>
        <template v-else-if="layout === 'vertical'">
            <FieldLabel
                v-if="props.label"
                :for="fieldId"
            >
                {{ props.label }}{{ props.required ? '*' : '' }}
            </FieldLabel>

            <FieldDescription v-if="props.description">
                {{ props.description }}
            </FieldDescription>

            <FieldContent>
                <slot v-bind="slotProps"></slot>
            </FieldContent>

            <FieldError
                v-if="props.errors && props.errors.length > 0"
                :errors="props.errors"
            />
        </template>
        <template v-else>
            <FieldContent v-if="props.label || props.description || (props.errors && props.errors.length > 0)">
                <FieldLabel
                    v-if="props.label"
                    :for="fieldId"
                >
                    {{ props.label }}{{ props.required ? '*' : '' }}
                </FieldLabel>

                <FieldDescription v-if="props.description">
                    {{ props.description }}
                </FieldDescription>

                <FieldError
                    v-if="props.errors && props.errors.length > 0"
                    :errors="props.errors"
                />
            </FieldContent>

            <slot v-bind="slotProps"></slot>
        </template>
    </Field>
</template>

<style>
@reference "@/assets/styles/index.css";

:where(.field-row) {
    display: flex;
    flex-flow: row wrap;
    gap: var(--field-gap);

    > * {
        flex: 1;
        min-width: 0;

        &[data-slot='field'].compact {
            flex: none;
        }
    }
}

:where(.field-col) {
    display: flex;
    flex-flow: column;
    gap: var(--field-gap);

    > * {
        flex: 1;
        min-width: 0;
    }
}
</style>
